import type { ComplianceDocumentsLocaleKey } from './localeNormalize'

const LEX_SUPPORTED_SECTION_LOCALES = [
  'en-GB',
  'de-DE',
  'zh-CN',
] as const satisfies readonly ComplianceDocumentsLocaleKey[]

const LEX_CANONICAL_BY_LANGUAGE: Record<string, ComplianceDocumentsLocaleKey> =
  {
    en: 'en-GB',
    de: 'de-DE',
    zh: 'zh-CN',
  }

/** Default region for common unsupported languages when only a language subtag is given. */
const UNSUPPORTED_LANGUAGE_DEFAULT_REGION: Record<string, string> = {
  it: 'IT',
  fr: 'FR',
  es: 'ES',
  pt: 'PT',
  nl: 'NL',
  pl: 'PL',
  sv: 'SE',
  da: 'DK',
  fi: 'FI',
  no: 'NO',
  nb: 'NO',
  nn: 'NO',
  ja: 'JP',
  ko: 'KR',
  ru: 'RU',
  cs: 'CZ',
  hu: 'HU',
  ro: 'RO',
  tr: 'TR',
}

function isValidRegion(region: string): boolean {
  return /^[A-Z]{2}$/.test(region) || /^\d{3}$/.test(region)
}

function formatLanguageRegion(language: string, region: string): string {
  return `${language}-${region}`
}

/**
 * Normalize a detected section source locale to BCP-47 language-region form.
 * Lex-supported languages map to en-GB / de-DE / zh-CN; other known languages
 * map to a conventional region. Returns undefined for empty/invalid input.
 * Never collapses unknown tags to en-GB.
 */
export function normalizeStandardSectionSourceLocale(
  input: string | undefined | null
): string | undefined {
  if (input == null) {
    return undefined
  }

  const trimmed = input.trim()
  if (trimmed === '') {
    return undefined
  }

  if (/[,;|]/.test(trimmed)) {
    return undefined
  }

  const parts = trimmed.replace(/_/g, '-').split('-')
  const language = parts[0]?.toLowerCase()
  if (language == null || !/^[a-z]{2,3}$/.test(language)) {
    return undefined
  }

  const lexCanonical = LEX_CANONICAL_BY_LANGUAGE[language]
  if (lexCanonical != null) {
    return lexCanonical
  }

  const regionPart = parts[1]
  if (regionPart != null && regionPart !== '') {
    const region =
      regionPart.length === 2 ? regionPart.toUpperCase() : regionPart
    if (!isValidRegion(region)) {
      return undefined
    }
    return formatLanguageRegion(language, region)
  }

  const defaultRegion = UNSUPPORTED_LANGUAGE_DEFAULT_REGION[language]
  if (defaultRegion != null) {
    return formatLanguageRegion(language, defaultRegion)
  }

  return undefined
}

export function isLexSupportedSectionLocale(
  locale: string | undefined
): locale is ComplianceDocumentsLocaleKey {
  if (locale == null) {
    return false
  }
  return (LEX_SUPPORTED_SECTION_LOCALES as readonly string[]).includes(locale)
}

/** Drop supported Lex locales that match a supported section source locale. */
export function filterTranslationLocalesExcludingSupportedSource<
  T extends string,
>(sourceLocale: string | undefined, targets: readonly T[]): T[] {
  const normalizedSource = normalizeStandardSectionSourceLocale(sourceLocale)
  if (
    normalizedSource == null ||
    !isLexSupportedSectionLocale(normalizedSource)
  ) {
    return [...targets]
  }
  return targets.filter((target) => target !== normalizedSource)
}
